import { useEffect } from "react";

import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";

import { SIDE_ID } from "@/features/product-variant-attributes";
import OutletAvailabilitySection from "../product-form/outlet-availability-section";
import ProductAddOnSection from "../product-form/product-add-on-section";
import GeneralInformationSection from "./general-information-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./lens-product-form-constants";
import LensProductVariantGroupsSection from "./lens-product-variant-groups-section";

export const LensProductForm = ({
  disableStockKeepingUnitPrefix = false,
  defaultValues,
  onSubmit,
  renderActionButton,
}) => {
  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });
  const { reset, handleSubmit, formState } = methods;

  useEffect(() => {
    if (!defaultValues) {
      return;
    }

    reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  const onFormSubmit = async (data) => {
    // Check if the "Sisi" attribute is checked
    const isSideChecked = data.variantAttributes?.some(
      (attr) => attr.id === SIDE_ID && attr.checked,
    );

    const payload = {
      name: data.name,
      status: data.status,
      skuPrefix: data.stockKeepingUnitPrefix,
      productCategoryId: data.productCategory.id,
      unitOfMeasurementId: data.unitOfMeasurement.id,
      productAddOns: data.productAddOns,
      lensProductVariantGroups: data.lensProductVariantGroups.map((group) => ({
        ...group,
        ...(isSideChecked ? { side: "R,L" } : {}),
      })),
      availableToAllOutlets: data.availableToAllOutlets,
      outletIds: data.availableToAllOutlets
        ? []
        : Object.keys(data.outlets).filter((key) => data.outlets[key]),
    };

    await onSubmit(payload);
  };

  return (
    <FormProvider {...methods}>
      <Stack
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        spacing={3}
      >
        <GeneralInformationSection
          disableStockKeepingUnitPrefix={disableStockKeepingUnitPrefix}
        />
        <LensProductVariantGroupsSection />
        <ProductAddOnSection />
        <OutletAvailabilitySection />
        {renderActionButton({ isSubmitting: formState.isSubmitting })}
      </Stack>
    </FormProvider>
  );
};

LensProductForm.propTypes = {
  disableStockKeepingUnitPrefix: PropTypes.bool,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  renderActionButton: PropTypes.func.isRequired,
};
